'use client';

import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth';
import { useToast } from './Toast';
import { useFeatureFlags } from '@/lib/useFeatureFlags';
import ComingSoonModal from './ComingSoonModal';

interface SaveButtonProps {
  contentType: 'insight' | 'framework' | 'comparison' | 'code' | 'architecture' | 'template' | 'case-study' | 'insights' | 'frameworks' | 'comparisons' | 'architectures' | 'templates' | 'case-studies';
  contentSlug: string;
  className?: string;
  showLabel?: boolean;
}

// Normalize plural content types to singular (matching API)
const normalizeContentType = (type: string): string => {
  const map: Record<string, string> = {
    'insights': 'insight',
    'frameworks': 'framework',
    'comparisons': 'comparison',
    'architectures': 'architecture',
    'templates': 'template',
    'case-studies': 'case-study',
  };
  return map[type] || type;
};

export default function SaveButton({
  contentType: rawContentType,
  contentSlug,
  className = '',
  showLabel = true
}: SaveButtonProps) {
  const { isAuthenticated, login } = useAuth();
  const { showToast } = useToast();
  const { flags } = useFeatureFlags();
  const [isSaved, setIsSaved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showComingSoon, setShowComingSoon] = useState(false);

  // Normalize content type to singular form
  const contentType = normalizeContentType(rawContentType);

  // Check if content is already saved
  useEffect(() => {
    if (!isAuthenticated) {
      setIsSaved(false);
      return;
    }

    async function checkSaved() {
      try {
        const response = await fetch(`/api/secure/saved-content?type=${contentType}`, {
          credentials: 'include'
        });

        if (response.ok) {
          const data = await response.json();
          const saved = data.data.some(
            (item: any) => item.content_slug === contentSlug && item.content_type === contentType
          );
          setIsSaved(saved);
        }
      } catch (error) {
        console.error('Failed to check if content is saved:', error);
      }
    }

    checkSaved();
  }, [isAuthenticated, contentType, contentSlug]);

  const handleSave = async () => {
    if (!isAuthenticated) {
      // Check if signup is allowed
      if (!flags.signUpAllowed) {
        setShowComingSoon(true);
        return;
      }
      // Signup is allowed, redirect to login
      login({ screenHint: 'signup' });
      return;
    }

    setIsLoading(true);

    try {
      if (isSaved) {
        // Unsave
        const response = await fetch(`/api/secure/saved-content?type=${contentType}&slug=${contentSlug}`, {
          method: 'DELETE',
          credentials: 'include'
        });

        if (response.ok) {
          setIsSaved(false);
          showToast('Removed from saved content', 'info');
        } else {
          throw new Error('Failed to remove from saved');
        }
      } else {
        // Save
        const response = await fetch('/api/secure/saved-content', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            contentType,
            contentSlug
          })
        });

        if (response.ok) {
          setIsSaved(true);
          showToast('Saved for later!', 'success');
        } else {
          throw new Error('Failed to save');
        }
      }
    } catch (error) {
      console.error('Save error:', error);
      showToast('Failed to save content. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleSave}
        disabled={isLoading}
        className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border transition-all hover:scale-105 active:scale-95 ${
          isSaved
            ? 'bg-accent-muted border-accent text-accent hover:bg-accent-muted'
            : 'bg-background border-border text-foreground hover:border-border-muted0 hover:bg-surface'
        } disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 ${className}`}
        aria-label={isSaved ? 'Remove from saved' : 'Save for later'}
      >
        <svg
          className={`w-5 h-5 transition-transform ${isSaved ? 'animate-scale-in' : ''}`}
          fill={isSaved ? 'currentColor' : 'none'}
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z"
          />
        </svg>
        {showLabel && (
          <span className="text-sm font-medium">
            {isLoading ? (
              <>
                <span className="animate-pulse">Saving...</span>
              </>
            ) : !isAuthenticated ? (
              'Sign up to save'
            ) : isSaved ? (
              '✓ Saved'
            ) : (
              'Save'
            )}
          </span>
        )}
      </button>
      <ComingSoonModal isOpen={showComingSoon} onClose={() => setShowComingSoon(false)} type="signup" />
    </>
  );
}
